/**
 * Canonical English copy for every customer-facing in-app string in
 * Manos. The voice + tone doc at `docs/voice-and-tone.md` is the
 * rulebook; this file is the canonical source of truth that reflects
 * those rules in actual sentences.
 *
 * Voice in one line: a competent mate sitting next to a tradesperson
 * who's running their business. Useful when they need it, out of the
 * way when they don't, never sells, never grovels.
 *
 * Conventions:
 *   - Group by surface domain (tour, empty, toast, error, button,
 *     dialog, label, hint). Sub-groups by feature only when the
 *     surface gets crowded.
 *   - Sentence case throughout. Brand names (Manos, Stripe, Xero,
 *     QuickBooks) capitalised; everything else not.
 *   - Placeholders use `{{name}}` -- no curly braces inside the
 *     string for any other reason.
 *   - Strings are TEXT only. JSX wrapping (bold, links) lives at
 *     the call site so translators only see plain text.
 *   - Money / dates / numbers are formatted by `@barrykilby/geo`
 *     and inlined via placeholders, never hard-coded into strings.
 *   - No exclamation marks except in genuine first-time wins. None
 *     in this file unless that bar is met.
 *   - Customer-of-the-customer = "customer". Trader's logged-in
 *     identity = "you". Trader's profile = "your business".
 *
 * `as const` so every string narrows to its literal in the type
 * system -- enables exhaustive autocomplete from t().
 */
export const en = {
  // ─── Guided tours ────────────────────────────────────────────
  tour: {
    welcome: {
      title: "Welcome",
      body: "A 60-second tour to show you where things live. Skip if you'd rather get going.",
    },
    inbox: {
      title: "Inbox",
      body: "Anything that needs a decision today -- new enquiries, replies, jobs in flight. Start here.",
    },
    quotes: {
      title: "Quotes",
      body: "Price up a job here. Your customer gets a link they can accept on their phone.",
    },
    quotesStart: {
      title: "Start a quote",
      body: "Pick the customer, add what you're charging for, send it. They get a link on their phone -- you'll see when they accept.",
    },
    schedule: {
      title: "Your week",
      body: "Jobs by day. Drag a job onto a time to book it in.",
    },
    quickCapture: {
      title: "Quick add",
      body: "Speak or type a note. We'll turn it into a job. Works on your phone too.",
    },
  },

  // ─── Empty states ────────────────────────────────────────────
  empty: {
    inboxAllCaughtUp: "Nothing waiting. Catch your breath.",
    quotesNoneYet: "No quotes yet. Press {{action}} above to send your first one.",
    invoicesNoneYet: "No invoices yet. Press {{action}} above to send your first one.",
    clientsNoneYet: "No customers yet. Press {{action}} above to add one.",
    transactionsNoneYet: "No transactions yet. Card payments show up here once you've taken one.",
    quotesInFlight: "Quotes you've drafted or sent show up here.",
    invoicesEmptyDescription: "Create your first invoice to get started.",
    invoicesNoStatus: "No {{status}} invoices found.",
    creditNotesEmpty:
      'Credit notes reverse invoices after issue. Open an invoice and press "Issue credit note" to create one.',
    clientsEmptyDescription:
      "Add your first customer, or import a spreadsheet if you're moving over from another system.",
    contactsEmptyDescription:
      "Suppliers, subcontractors, inspectors -- everyone you work with. Import from your phone's contacts or add them one at a time.",
    clientsNoMatch: 'No customers match "{{query}}"',
    contactsNoMatch: 'No contacts match "{{query}}"',
  },

  // ─── Toasts (success + ambient) ──────────────────────────────
  toast: {
    saved: "Saved.",
    sent: "Sent.",
    quoteSent: "Quote sent to {{customerName}}. We'll let you know when they open it.",
    invoiceSent: "Invoice sent to {{customerName}}. We'll let you know when they open it.",
    cardPaymentsOn: {
      title: "Card payments are on",
      body: "Customers can pay you by card or bank transfer now.",
    },
    detailsSubmitted: {
      title: "Details submitted",
      body: "Stripe is checking your account -- usually a few minutes, sometimes a day.",
    },
  },

  // ─── Subscription / billing ──────────────────────────────────
  // Customer-facing copy for the most financially sensitive moments
  // in the app. Voice rules apply hardest here -- brief, no
  // accounting jargon, no celebration of trivial wins. Errors give a
  // recovery path (email {{salesEmail}}) when there is one.
  subscription: {
    planUpdated: "Plan updated. The difference will appear on your next invoice.",
    paymentNeedsConfirm:
      "Payment needs confirmation -- finish the change from your card list below.",
    planUpdateFailed:
      "Couldn't update your plan. Try again or email {{salesEmail}}.",
    subscriptionActive: "You're on. Subscription active.",
    checkoutCancelled: "Checkout cancelled. Nothing has changed.",
    cardSaved: "Card saved.",
    cardSaveFailed:
      "Couldn't save that card. Try again or email {{salesEmail}}.",
    cancelScheduled: "Subscription cancels at period end.",
    cancelFailed:
      "Couldn't cancel your subscription. Try again or email {{salesEmail}}.",
    defaultCardUpdated: "Default card updated.",
    defaultCardFailed: "Couldn't update default card.",
    cardRemoved: "Card removed.",
    cardRemoveFailed: "Couldn't remove card.",
    topupComplete: "Top-up complete. Credits added.",
    topupCancelled: "Top-up cancelled. Nothing has changed.",
  },

  // ─── Errors ──────────────────────────────────────────────────
  errors: {
    loadFailed: "Couldn't load that. Refresh to try again, or come back in a minute.",
    actionFailed: "Couldn't do that. Refresh and check before trying again.",
    markPaidConflict:
      "Couldn't mark this paid -- it may already show as paid. Refresh and check.",
    sessionExpired: "Your session has expired. Sign in again to continue.",
    onboardingIncomplete: "Couldn't finish Stripe setup. Press the button to pick up where you left off.",
    inboxLoadFailed:
      "Couldn't load your inbox. Refresh to try again, or come back in a minute.",
    invoicesLoadFailed:
      "Couldn't load your invoices. Refresh to try again, or come back in a minute.",
    invoicesListFailed: "Couldn't load invoices. Refresh and try again.",
    creditNotesLoadFailed:
      "Couldn't load your credit notes. Refresh to try again.",
    clientsLoadFailed:
      "Couldn't load your customers. Refresh to try again, or come back in a minute.",
    clientsListFailed: "Couldn't load customers. Refresh and try again.",
    contactsListFailed: "Couldn't load contacts. Refresh and try again.",
    parseFailed: "Couldn't read that. Fill in the details below by hand.",
    clarifyFailed:
      "Couldn't use the clarification. Check the fields below by hand.",
    createWorkItemSilent:
      "Couldn't save that. Check the details and try again, or refresh the page.",
    saveFailedGeneric: "Couldn't save that. Refresh and try again.",
    earningsNoProfile:
      "Customer payments show up here once your business profile is set up.",
    earningsPayoutsLoadFailed:
      "Couldn't load upcoming payouts. Refresh to try again.",
    earningsLoadFailed:
      "Couldn't load customer payments. Refresh to try again.",
    earningsNotAuthenticated:
      "Your session has expired. Sign in again to view payments.",
    voiceNotSupported: "Voice input isn't supported in this browser.",
    voiceTryOtherBrowsers: "Try Chrome, Edge, or Brave.",
    voiceMicDenied:
      "Microphone permission denied. Enable mic access in your browser site settings.",
    voiceServiceBlocked:
      "Your browser blocked speech recognition. Brave: enable 'Google services for push messaging' in brave://settings/privacy.",
    voiceNoSpeech:
      "No speech detected. Try speaking louder or closer to the mic.",
    voiceNoMic:
      "No microphone found. Check that one is connected and enabled.",
    voiceNetwork: "Speech service unreachable. Check your internet connection.",
    voiceAborted: "Voice input stopped.",
    voiceFailed: "Voice input failed ({{code}}).",
    voiceCouldNotStart: "Couldn't start voice input.",
  },

  // ─── Buttons ─────────────────────────────────────────────────
  buttons: {
    newQuote: "New quote",
    newInvoice: "New invoice",
    newClient: "New customer",
    newContact: "New contact",
    sendQuote: "Send quote",
    sendInvoice: "Send invoice",
    markPaid: "Mark paid",
    save: "Save",
    cancel: "Cancel",
    setUpCardPayments: "Set up card payments",
    continueSetup: "Continue setup",
    signIn: "Sign in",
    signOut: "Sign out",
    refresh: "Refresh",
    goHome: "Go home",
    goToInbox: "Go to inbox",
    backToInvoices: "Back to invoices",
    goToSettings: "Go to settings",
    importSpreadsheet: "Import",
    importVCardOrCsv: "Import vCard or CSV",
    importSpreadsheetSecondary: "Import spreadsheet",
    addContact: "Add a contact",
    print: "Print",
    create: "Create",
    creating: "Creating...",
    parse: "Continue",
    parsing: "Working on it...",
    understanding: "Understanding your input...",
    update: "Update",
    confirm: "Confirm",
    skipToForm: "Skip to form",
    startOver: "Start over",
    back: "Back",
    captureFirstJob: "Capture your first job",
    voice: "Voice",
    stop: "Stop",
  },

  // ─── Confirmation dialogs (destructive actions) ──────────────
  dialog: {
    deleteClient: {
      title: "Delete {{customerName}}?",
      body: "Their {{jobsCount}} {{jobsLabel}} and {{invoicesCount}} {{invoicesLabel}} will keep their name attached.",
      confirm: "Delete customer",
    },
    deleteQuote: {
      title: "Delete this quote?",
      body: "Your customer won't be able to accept it any more. The job stays.",
      confirm: "Delete quote",
    },
    deleteInvoice: {
      title: "Delete this invoice?",
      body: "If it's already been sent, your customer can still see it from the link they have.",
      confirm: "Delete invoice",
    },
    cancelSubscription: {
      title: "Cancel your Manos plan?",
      body: "You'll keep access until the end of your current billing period. Customer payments stop straight away.",
      confirm: "Cancel plan",
    },
  },

  // ─── Field labels + placeholders ─────────────────────────────
  label: {
    customerName: "Customer",
    jobTitle: "Job",
    scheduledStart: "Scheduled start",
    scheduledEnd: "Scheduled end",
    description: "Description",
    location: "Location",
  },

  // ─── Inline hints (one-liners under inputs / next to actions) ─
  hint: {
    customerNotInList:
      "Not in your list? Add them right here -- you don't have to leave this screen.",
    paymentMethodsAvailable:
      "Your customers can pay by card, bank transfer, or buy-now-pay-later at checkout.",
    sameAsBilling:
      "We'll use your customer's billing address as the job location.",
  },

  // ─── Placeholders ────────────────────────────────────────────
  placeholder: {
    searchClients: "Search customers...",
    searchContacts: "Search contacts...",
    quickCapture: "Quick capture",
    captureWhenListening: "Listening... speak now",
    captureFallback: "Type anything...",
    clarifyFirstPass: "Dictate or type what needs doing...",
    clarifyFollowUp:
      "e.g. It's 3pm not 3am, or Mrs Hollins at 14 Hanley Road, or just press Confirm if it's right",
    workTitle: "Short title for this work",
    clientSearchOrCreate: "Search or create a customer...",
    workDescription: "What needs to be done...",
  },

  // ─── Badges ──────────────────────────────────────────────────
  badge: {
    linked: "Linked",
    voided: "Voided",
    active: "Active",
    onlyMe: "Only me",
    team: "Team",
    visibleToTeam: "Visible to team",
  },

  // ─── Inbox ───────────────────────────────────────────────────
  inbox: {
    pageTitle: "Inbox",
    headerLoadFailed: "Inbox couldn't load",
    profileIncomplete: {
      title: "Complete your profile",
      body: "Set up your business profile to start using Manos.",
    },
    sections: {
      needsAction: "Needs action",
      today: "Today",
      errands: "Errands",
      awaitingResponse: "Awaiting response",
      overdue: "Overdue",
      recentlyCompleted: "Recently completed",
      reminders: "Reminders",
    },
    empty: {
      needsAction: "Nothing needs your attention right now.",
      today: "No jobs scheduled for today.",
      errands: "No open errands.",
      awaitingResponse: "No quotes awaiting customer response.",
      overdue: "No overdue invoices.",
      recentlyCompleted: "Nothing finished yet.",
    },
    firstRun: {
      title: "Your inbox is empty",
      body: "Capture your first piece of work to get started. Got a phone call? A text from a customer? Press the button below and type or dictate what needs doing.",
      keyboardHint:
        "Or press {{shortcut}} to search, or use the + button on mobile.",
    },
    allCaughtUp: "You're all caught up.",
    untitled: "Untitled",
    untitledErrand: "Untitled errand",
    unknownClient: "Unknown customer",
    forStandaloneTask: "a standalone task",
    forJobLabel: "for",
    quickCaptureAria: "Quick capture",
    markErrandDoneAria: 'Mark "{{title}}" done',
    errandDoneToast: "Errand done.",
    errandDoneFailed: "Couldn't mark done. Refresh and try again.",
    title: "Inbox",
    filter: {
      active: "Active",
      trash: "Trash",
    },
    trash: {
      toastMoved: "Moved to Trash",
      restored: "Restored",
      restoreFailed: "Couldn't restore",
      blocked: "Can't trash from this stage",
      empty: "Nothing in Trash. Items move here when you trash them, and stay here until you restore them.",
    },
  },

  // ─── Quick capture (header pill + capture page) ──────────────
  capture: {
    header: {
      title: "Quick capture",
      stepInput: "Describe the work in your own words",
      stepReview: "Review and edit the details",
    },
    processing: {
      title: "Processing your capture...",
      body: "Reading through the note, matching it to a customer, pulling out the address and what needs doing. Hold tight.",
    },
    inputLabel: "What needs doing?",
    listeningHint:
      "Speaking... press Stop when finished, then press Continue.",
    typingHint:
      "Type or dictate. Include the customer name, address, what needs doing, and any dates. We'll pull out the details.",
    cancel: "Cancel",
    goBack: "Go back",
    clarify: {
      parseFailedHeading: "I couldn't pick out a job from that.",
      receivedHeading: "Here's what I received",
      parseFailedHint:
        "If that's wrong, dictate or type the note again below. If it's right but I still couldn't make sense of it, try adding more detail (customer name, address, what needs doing).",
      heardHeading: "Here's what I think I heard:",
      fieldClient: "Customer",
      fieldJob: "Job",
      fieldAddress: "Address",
      fieldWhen: "When",
      fieldType: "Type",
      uncertainHeading: "I wasn't sure about:",
      multipleClients:
        'I found {{count}} customers called "{{name}}" -- which one did you mean?',
      reentryLabelFailed: "Try again in your own words",
      reentryLabel: "Tell me what's not right, or just confirm",
    },
    review: {
      needsAttention: "Needs your attention:",
      multipleClientsTitle:
        'Multiple customers match "{{name}}". Which one did you mean?',
      noneCreateNew:
        'None of these -- create a new customer called "{{name}}"',
      lowConfidenceTitle: "I'm not sure I heard that right.",
      lowConfidenceBody: "Check the fields below carefully before saving.",
      lowConfidenceAck: "I've checked the details",
      titleRequired: "Title is required",
      pickClient: "Pick which customer this is for",
      tickAck: "Tick the 'I've checked the details' box to save",
      whatYouEntered: "What you entered",
      capturedContact: "Captured: {{contact}}",
      sameAsBilling: "Same as billing address",
    },
    fields: {
      title: "Title",
      client: "Customer",
      location: "Location",
      description: "Description",
      scheduledStart: "Scheduled start",
      scheduledEnd: "Scheduled end",
    },
    pill: {
      processingAria: "Processing...",
      stopDictationAria: "Stop dictation",
      sendAria: "Send (Enter)",
      dictateAria: "Dictate",
      dictationUnsupported:
        "Dictation isn't supported in this browser -- type instead",
    },
    toasts: {
      workItemSaved: "Saved.",
      workItemOpening: "Opening #{{id}}...",
      createFailedTitle: "Couldn't save",
      voiceFailedTitle: "Voice input failed",
      voiceCouldNotStartTitle: "Couldn't start voice input",
    },
  },

  // ─── Quotes list ─────────────────────────────────────────────
  quotes: {
    pageTitle: "Quotes",
    newDialogTitle: "Start a new quote",
    newDialogTitleFor: "Start a new quote for {{name}}",
    newDialogDescription:
      "Pick a saved template if it fits, or describe the job and let Apprentice draft it.",
    newDialogTemplateLabel: "Use a template",
    newDialogTemplatePlaceholder: "Pick a template...",
    newDialogDictateLabel: "Or describe the work",
    newDialogCustomerLabel: "Customer",
    newDialogContinue: "Continue",
    newDialogOpenQuote: "Open quote",
    newDialogTemplatesEmptyTitle: "No templates yet",
    newDialogTemplatesEmptyBody:
      "Save time on repeat work by creating a quote template once and reusing it.",
    newDialogCreateTemplate: "Create your first template",
    newDialogAddTemplateLink: "Add a new template",
    newDialogLoadedFromTemplate: "Loaded from {{name}}",
    emptyTitle: "No quotes in flight",
    truncationNotice:
      "Showing your 250 most recent quotes. Use filters or archive old jobs to see the rest.",
    columns: {
      job: "Job",
      client: "Customer",
      stage: "Stage",
      estimate: "Estimate",
      lastActivity: "Last activity",
    },
    untitledJob: "Untitled job",
  },

  // ─── Invoices list + credit notes ────────────────────────────
  invoices: {
    pageTitle: "Invoices",
    summary: {
      outstanding: "Outstanding",
      overdue: "Overdue",
      paid: "Paid",
      credited: "Credited",
      invoiceOne: "{{count}} invoice",
      invoiceMany: "{{count}} invoices",
    },
    columns: {
      invoiceNumber: "Invoice #",
      client: "Customer",
      status: "Status",
      issued: "Issued",
      due: "Due",
      total: "Total",
    },
    emptyTitle: "No invoices yet",
    truncationNotice:
      "Showing first 250 invoices. Use the status filter to narrow results.",
    headerLoadFailed: "Invoices couldn't load",
    headerLoadFailedBody:
      "Couldn't fetch your invoices. Refresh, or head back to the inbox.",
    mobileCardDraft: "Draft",
    mobileCardNoClient: "No customer",
    mobileCardDuePrefix: "Due",
    inboxFallback: "Oops",
    filter: {
      active: "Active",
      trash: "Trash",
    },
    trash: {
      toastMoved: "Invoice moved to Trash",
      restored: "Invoice restored",
      restoreFailed: "Couldn't restore invoice",
      empty: "Nothing in Trash. Invoices move here when you trash them, and stay here until you restore them.",
    },
    credits: {
      pageTitle: "Credit notes",
      emptyTitle: "No credit notes yet",
      headerLoadFailed: "Credit notes couldn't load",
      headerLoadFailedBody:
        "Couldn't fetch your credit notes. Refresh to try again.",
      summary: {
        active: "Active credits",
        voided: "Voided",
        creditNoteOne: "{{count}} credit note",
        creditNoteMany: "{{count}} credit notes",
        creditNoteUnit: "credit note",
        creditNoteUnitPlural: "credit notes",
      },
      columns: {
        cnNumber: "CN number",
        originalInvoice: "Original invoice",
        client: "Customer",
        amount: "Amount",
        reason: "Reason",
        issued: "Issued",
        status: "Status",
      },
      truncationNotice: "Showing first 250 credit notes.",
      mobileCardNoClient: "No customer",
    },
  },

  // ─── Clients (customers) list ────────────────────────────────
  clients: {
    pageTitle: "Customers",
    emptyTitle: "No customers yet",
    headerLoadFailed: "Customers couldn't load",
    headerLoadFailedBody:
      "Couldn't fetch your customers. Refresh, or head back to the home screen.",
    columns: {
      name: "Name",
      contact: "Contact",
      notes: "Notes",
      lastActive: "Last active",
    },
    truncationNotice:
      "Showing first 250 customers. Use search to find specific ones.",
    filter: {
      active: "Active",
      trash: "Trash",
    },
    trash: {
      toastMoved: "Customer moved to Trash",
      restored: "Customer restored",
      restoreFailed: "Couldn't restore customer",
      empty: "Nothing in Trash. Customers move here when you trash them, and stay here until you restore them.",
      deletedLabel: "Deleted",
    },
  },

  // ─── Contacts list ───────────────────────────────────────────
  contacts: {
    pageTitle: "Contacts",
    emptyTitle: "No contacts yet",
    columns: {
      name: "Name",
      company: "Company",
      contact: "Contact",
      visibility: "Visibility",
    },
    truncationNotice:
      "Showing first 250 contacts. Use search to find specific entries.",
    linkedTitle: "Linked to a Manos platform profile",
  },

  // ─── Import (settings/import) ────────────────────────────────
  // Shared across two flows: spreadsheet upload + sample-data load.
  // The picker host on /settings/import shows two tiles; each flow
  // funnels into the same ResultsSummary shape.
  import: {
    picker: {
      title: "Import",
      description: "Bring data into Manos. Pick where it's coming from.",
      spreadsheet: {
        title: "From a spreadsheet",
        description:
          "CSV or Excel from your old tool, your accountant, or a kitchen-table list.",
      },
      sample: {
        title: "From sample data",
        description:
          "Drop in realistic customers, jobs, and invoices so you can have a poke around. Clear it any time.",
      },
    },

    spreadsheet: {
      typePickerTitle: "What are you importing?",
      typePickerHint:
        "Pick one. You can come back and import another type after.",
      autoMatch: {
        looksLike:
          "This looks like a {{label}} import. {{matched}} required columns matched.",
        partial:
          "Looks like a {{label}} import, but {{missing}} {{missingPlural}}.",
        partialMissingOne: "is missing",
        partialMissingMany: "are missing",
        ambiguous:
          "We couldn't tell which type this is. Pick one to continue.",
        none:
          "We couldn't tell what this file is. Pick a type, or grab a fresh template below.",
        wrongType: "Wrong type?",
      },
      types: {
        client: {
          title: "Customers",
          description: "Names, phones, emails, addresses.",
        },
        workItem: {
          title: "Jobs",
          description:
            "Scheduled work linked to existing customers.",
        },
        invoice: {
          title: "Invoices",
          description:
            "One line per invoice. Totals only for now.",
        },
      },
      drop: {
        idle: "Drop your file here, or press to browse.",
        ready:
          "{{filename}} -- press or drop another to replace.",
      },
      submit: {
        idle: "Import {{count}} {{label}}",
        idleWithSkip: "Import {{count}} {{label}} (skip {{skip}})",
      },
      template: {
        downloadCsv: "Download CSV",
        downloadXlsx: "Download Excel",
      },
    },

    sample: {
      intro:
        "Pick what you want to see, then we'll fill {{businessName}} with realistic data.",
      tradeLabel: "Show me data for...",
      tradeHint:
        "This only changes the sample data, not your account.",
      scaleLabel: "How much?",
      scales: {
        small: {
          title: "Small",
          body: "A handful of customers and a couple of jobs.",
        },
        medium: {
          title: "Medium",
          body: "A regular working week.",
        },
        large: {
          title: "Large",
          body: "A few months of history.",
        },
        xlarge: {
          title: "Extra large",
          body: "Lots. Takes longer to load.",
        },
      },
      confirmFresh: {
        title: "Load sample {{trade}} data?",
        body:
          "We'll add about {{customers}} customers, {{jobs}} jobs, and {{invoices}} invoices, all marked as sample data so you can clear them later.",
        confirm: "Load sample data",
      },
      confirmReplace: {
        title: "Replace the existing sample data?",
        body:
          "You already have sample data loaded. We'll clear it first, then load the new set. Your real customers, jobs, and invoices won't be touched.",
        confirm: "Replace it",
      },
      loading: {
        title: "Loading sample data",
        body: "About 20 seconds. Hang on.",
      },
      clear: {
        buttonIdle: "Clear sample data",
        buttonEmpty: "Nothing to clear",
        confirmTitle: "Clear the sample data?",
        confirmBody:
          "We'll remove anything marked as sample data. Your real customers, jobs, and invoices stay put.",
        confirm: "Clear it",
        success: "Sample data cleared.",
      },
      rateLimited:
        "You've loaded sample data three times in the last minute. Take a breath, then try again.",
      disabled: {
        title: "Sample data is off on this account",
        body:
          "You turned this off when your account had real data on it.",
        enable: "Turn it back on",
      },
    },

    progress: {
      // Live-update copy while a fan-out import is in flight. The UI
      // subscribes to the importBatch row via useFindOne with
      // `live: true`; counts update without polling.
      title: "Importing...",
      sampleTitle: "Loading sample data...",
      countsLine: "{{processed}} of {{total}}",
      countsLineWithErrors:
        "{{processed}} of {{total}} ({{errors}} with errors)",
      starting: "Starting...",
      finishing: "Finishing up...",
      failed: {
        title: "Import didn't finish",
        body: "Something went wrong. The bits that landed are still saved.",
      },
    },

    results: {
      spreadsheetTitle: "Import results",
      sampleTitle: "Sample data loaded",
      counts: {
        clientCreated: "{{count}} new customer",
        clientsCreated: "{{count}} new customers",
        jobCreated: "{{count}} job created",
        jobsCreated: "{{count}} jobs created",
        invoiceCreated: "{{count}} invoice created",
        invoicesCreated: "{{count}} invoices created",
        matchedExisting: "{{count}} matched existing",
      },
      warnings: {
        duplicates:
          "{{count}} possible duplicates -- created but worth checking.",
        skippedNoClient:
          "{{count}} skipped because we couldn't find a matching customer.",
      },
      cta: {
        inbox: "Open the inbox",
        invoices: "See invoices",
        clients: "See customers",
        jobs: "See jobs",
        done: "Done",
      },
    },

    sources: {
      heading: "What's been imported",
      empty: "Nothing imported yet. Pick a way to start above.",
      spreadsheet: "{{count}} via spreadsheet",
      sample: "Sample data loaded",
      clearByLabel: "Clear",
      disableSample:
        "Disable sample data on this account",
      enableSample:
        "Allow sample data on this account",
    },

    toast: {
      importFailed:
        "Couldn't run that import. Refresh and try again, or come back in a minute.",
      clearFailed:
        "Couldn't clear that. Refresh and try again.",
      invalidFile: "We need a .csv or .xlsx file.",
      emptyFile: "That file looks empty.",
    },
  },

  // ─── Accreditations (settings + public profile) ──────────────
  accreditations: {
    title: "Accreditations",
    description:
      "Add the trade schemes and certificates you're registered with. They'll show on your public profile. We don't check them -- make sure yours are up to date.",
    empty: "Nothing added yet. Pick one to start.",
    addButton: "Add accreditation",
    removeButton: "Remove",
    picker: {
      title: "Add an accreditation",
      description: "Pick the scheme you're registered with.",
      searchPlaceholder: "Search accreditations...",
      relevant: "Relevant to your trade",
      everythingElse: "Everything else",
      noResults: "Nothing matches that search.",
    },
    entry: {
      title: "Your details",
      certificateNumber: "Certificate or membership number",
      certificateNumberPlaceholder: "Optional",
      issuedAt: "Issue date",
      expiresAt: "Expiry date",
      expiresAtAuto: "Suggested from issue date.",
      issuingBodyUrl: "Issuing body website",
      issuingBodyUrlPlaceholder: "https://...",
      save: "Add to my profile",
      cancel: "Back",
    },
    chip: {
      verifyLink: "Verify",
      expired: "Expired",
    },
    toast: {
      added: "Accreditation added.",
      updated: "Accreditation updated.",
      removed: "Accreditation removed.",
      saveFailed: "Couldn't save that. Refresh and try again.",
    },
    publicHeading: "Accreditations",
  },

  // ─── Accounting connections (Xero / QuickBooks / FreeAgent) ──
  // Product-voice strings for the Phase 1 accounting UI. Brief,
  // useful, no SaaS jargon. Cert reviewers will read some of these
  // (sync ledger, soft-mismatch confirm) so they have to read clean
  // to a UK accountant too -- "invoice", "VAT", "category", "code".
  accounting: {
    page: {
      connectionsTitle: "Accounting connections",
      connectionsDescription:
        "Connect one accounting tool and we'll push invoices, payments and expenses across. Disconnect any time -- nothing on the other side gets deleted.",
    },
    providers: {
      xero: "Xero",
      quickbooks: "QuickBooks Online",
      freeagent: "FreeAgent",
    },
    status: {
      active: "Connected",
      expired: "Needs reconnecting",
      revoked: "Revoked",
      disconnected: "Disconnected",
      unknown: "Unknown",
    },
    cta: {
      connect: "Connect {{provider}}",
      disconnect: "Disconnect",
      reconnect: "Reconnect",
      viewLedger: "View sync log",
      rerunMapping: "Edit mapping",
      backfill: "Backfill history",
    },
    card: {
      lastSync: "Last synced {{when}}",
      neverSynced: "Not synced yet",
      pushSuccessRate: "{{rate}} of recent pushes succeeded",
      noRecentPushes: "Nothing pushed yet",
      orgId: "Org id: {{id}}",
      sandbox: "Sandbox",
    },
    mapping: {
      title: "Map your categories",
      description:
        "We need to know where to file each line on an invoice or expense. Pick a category in {{provider}} for each kind of work; you can change this later.",
      sectionLabour: "Labour",
      sectionLabourHint: "Time billed -- callouts, hours, day rates.",
      sectionMaterials: "Materials",
      sectionMaterialsHint: "Parts, tools, anything you bought to do the job.",
      sectionOther: "Other",
      sectionOtherHint: "Mileage, congestion charge, anything that doesn't fit above.",
      placeholder: "Pick a category",
      saveAndContinue: "Save and continue",
      useDefaults: "Use defaults",
      defaultsExplain:
        "We'll pick sensible defaults -- you can change them later from the connection card.",
      saved: "Mapping saved.",
      saveFailed: "Couldn't save that. Refresh and try again.",
    },
    backfill: {
      title: "Backfill history to {{provider}}",
      prompt:
        "You have {{count}} invoices in Manos that aren't in {{provider}} yet. Push them across? We'll do it in the background and let you know when it's done.",
      startButton: "Start backfill",
      skipButton: "Not now",
      progressHeading: "Pushing to {{provider}}",
      progressDetail: "{{done}} of {{total}} pushed",
      cancelButton: "Cancel",
      doneAllSucceeded: "{{count}} pushed across. Nothing else to do.",
      doneWithFailures:
        "{{done}} of {{total}} pushed. {{failed}} need a closer look -- check the sync log.",
      idleNote:
        "Backfill is opt-in. We won't move anything until you press start.",
      statusPending: "Pending",
      statusRunning: "Pushing",
      statusSucceeded: "Pushed",
      statusFailed: "Failed",
      failedReason: "Reason: {{reason}}",
    },
    ledger: {
      title: "Sync log",
      description:
        "The last 100 things we sent or pulled from this connection. Click a row to see exactly what went across.",
      empty: "Nothing synced yet.",
      filterEntity: "Entity",
      filterStatus: "Status",
      filterAll: "All",
      filterEntities: {
        invoice: "Invoices",
        contact: "Contacts",
        expense: "Expenses",
        payment: "Payments",
        client: "Customers",
      },
      filterStatuses: {
        succeeded: "Succeeded",
        failed: "Failed",
        rolledBack: "Rolled back",
      },
      colWhen: "When",
      colEntity: "Entity",
      colOperation: "Operation",
      colExternalId: "Provider id",
      colStatus: "Status",
      drillSent: "What we sent",
      drillReceived: "What came back",
    },
    softMismatch: {
      title: "Heads up about this connection",
      currencyBody:
        "{{orgName}} is set up for {{actualCurrency}}. Your Manos business is set up for {{expectedCurrency}}. If you connect anyway, we'll honour {{actualCurrency}} on anything we push to this org.",
      countryBody:
        "{{orgName}} is registered in {{actualCountry}}. Your Manos business is set up for {{expectedCountry}}. Continue if that's intentional -- some country-specific behaviour (CIS, VAT, 1099) follows the org.",
      featureBody:
        "{{orgName}} doesn't support {{feature}}. You can still connect, but the bits of Manos that rely on it won't push to this org.",
      continue: "Continue anyway",
      cancel: "Pick a different org",
    },
    toast: {
      connected: "{{provider}} connected.",
      disconnected: "{{provider}} disconnected.",
      backfillStarted: "Backfill started. We'll keep you posted.",
      backfillCancelled: "Backfill cancelled.",
      backfillFailedToStart: "Couldn't start the backfill. Try again in a minute.",
      mappingSaved: "Mapping saved.",
    },
  },
} as const;

export type Strings = typeof en;
